import React, { useEffect, useState } from 'react';
import Button from 'components/crm/ui/Button';
import Modal from 'components/crm/ui/Modal';
import TextField from 'components/crm/ui/TextField';
import useText from 'hooks/useText';

export const NOT_DONE_REASONS = [
  'no_show',
  'canceled_by_client',
  'canceled_by_us',
  'postponed',
  'other',
];
export const MAX_NOT_DONE_DETAIL = 500;

/**
 * Why an appointment was not held, in words: its reason, or what was written
 * for « Autre »; '' for an appointment marked before reasons existed.
 */
export const notDoneReasonText = (reason, detail, text) => {
  if (reason === 'other') return detail?.trim() || text('reason_other');
  return NOT_DONE_REASONS.includes(reason) ? text(`reason_${reason}`) : '';
};

// Mounted while open only: each opening starts with no reason chosen.
const NotDoneChoice = ({ onCancel, onConfirm }) => {
  const { text } = useText('appointment');
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState('');
  const [error, setError] = useState('');
  // The Modal closes on Échap; marked handled first, so that the slide-over
  // around it stays open.
  useEffect(() => {
    const onKeyDown = (event) =>
      event.key === 'Escape' && event.preventDefault();
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const confirm = () => {
    if (!reason) return setError(text('notDoneReasonRequired'));
    const precision = reason === 'other' ? detail.trim() : '';
    if (reason === 'other' && !precision)
      return setError(text('notDoneDetailRequired'));
    return onConfirm(reason, precision);
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title={text('notDoneTitle')}
      width={420}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {text('notDoneCancel')}
          </Button>
          <Button onClick={confirm}>{text('notDoneConfirm')}</Button>
        </>
      }
    >
      <fieldset className="flex flex-col gap-3 text-sm text-body">
        <legend className="mb-3 text-sm font-medium text-ink">
          {text('notDoneReason')}
        </legend>
        {NOT_DONE_REASONS.map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="appointment-not-done-reason"
              value={value}
              checked={reason === value}
              onChange={() => {
                setReason(value);
                setError('');
              }}
            />
            {text(`reason_${value}`)}
          </label>
        ))}
      </fieldset>
      {reason === 'other' && (
        <TextField
          id="appointment-not-done-detail"
          label={text('notDoneDetail')}
          placeholder={text('notDoneDetailPlaceholder')}
          multiline
          maxLength={MAX_NOT_DONE_DETAIL}
          value={detail}
          onChange={(event) => {
            setDetail(event.target.value);
            setError('');
          }}
        />
      )}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </Modal>
  );
};

/**
 * « Non réalisé » of an appointment (`open`): its reason, one of
 * NOT_DONE_REASONS, and for « Autre » what happened, required. « Enregistrer »
 * hands (reason, detail) to `onConfirm`, detail '' unless « Autre »;
 * « Annuler », Échap or the backdrop call `onCancel`.
 */
const NotDoneDialog = ({ open, onCancel, onConfirm }) =>
  open ? <NotDoneChoice onCancel={onCancel} onConfirm={onConfirm} /> : null;

export default NotDoneDialog;
